import { Route, Switch, Link } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import { lazy, Suspense, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "./_core/hooks/useAuth";
import { getLoginUrl } from "./const";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { AdminLogin } from "./pages/AdminLogin";
import IGETProduct from "./pages/IGETProduct";
import IGETProductNew from "./pages/IGETProductNew";
import FAQ from "./pages/FAQ";
import BuyingGuide from "./pages/BuyingGuide";
import ProductRecommendationAdvanced from "./pages/ProductRecommendationAdvanced";
import ArticleDetail from "./pages/ArticleDetail";
import UserAccount from "./pages/UserAccount";
import PublishedProductPage from "./pages/PublishedProductPage";
import PublishedProductCategory from "./pages/PublishedProductCategory";
import PublishedProductGrid from "./pages/PublishedProductGrid";
import NotFound from "@/pages/NotFound";
import RetailerFinderPage from "./pages/RetailerFinderPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ProductInventory = lazy(() => import("./pages/ProductInventory"));
const ArticlesManagement = lazy(() => import("./pages/ArticlesManagement").then(({ ArticlesManagement: Component }) => ({ default: Component })));
const VisitorStats = lazy(() => import("./pages/VisitorStats"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const SupportSimulation = lazy(() => import("./pages/SupportSimulation"));
const ProductImportReview = lazy(() => import("./pages/ProductImportReview"));
const TobaccoLocationsManagement = lazy(() => import("./pages/TobaccoLocationsManagement"));
const ReviewModeration = lazy(() => import("./pages/ReviewModeration"));
const AuthModal = lazy(() => import("./components/AuthModal").then(({ AuthModal: Component }) => ({ default: Component })));

function Router() {
  return (
    <Suspense fallback={<main className="min-h-[50vh] px-4 py-16 text-center text-slate-500">Loading IGET information…</main>}>
      <Switch>
      <Route path="/products/iget" component={IGETProduct} />
      <Route path="/products/iget-new" component={IGETProductNew} />
      <Route path="/products/all" component={PublishedProductGrid} />
      <Route path="/product-pages/:slug" component={PublishedProductPage} />
      <Route path="/categories/:slug" component={PublishedProductCategory} />
      <Route path="/faq" component={FAQ} />
      <Route path="/buying-guide" component={BuyingGuide} />
      <Route path="/recommendation" component={ProductRecommendationAdvanced} />
      <Route path="/find-retailers" component={RetailerFinderPage} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-of-use" component={TermsOfUse} />
      <Route path="/articles/:id" component={ArticleDetail} />
      <Route path="/admin" component={AdminLogin} />
      <Route path="/admin/login" component={AdminLogin} />
      <Route path="/admin/inventory" component={ProductInventory} />
      <Route path="/admin/recommendation-stats" component={AdminDashboard} />
      <Route path="/admin/visitors" component={VisitorStats} />
      <Route path="/admin/articles" component={ArticlesManagement} />
      <Route path="/admin/users" component={UserManagement} />
      <Route path="/admin/support-simulation" component={SupportSimulation} />
      <Route path="/admin/product-imports" component={ProductImportReview} />
      <Route path="/admin/tobacco-locations" component={TobaccoLocationsManagement} />
      <Route path="/admin/reviews" component={ReviewModeration} />
      <Route path="/admin/product-imports/:jobId" component={ProductImportReview} />
      {/* Redirect /admin/user to /admin/users */}
      <Route path="/admin/user" component={UserManagement} />
      <Route path="/account" component={UserAccount} />
      <Route path="/" component={Home} />
      <Route path="/404" component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function Navigation() {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  // The public shell only needs cached token state; do not block first paint on auth.me.
  const { user } = useAuth({ skipInitialQuery: true });
  
  // Hide navigation on admin pages - 必须在 hooks 调用之后检查
  const isAdminPage = location.startsWith('/admin');
  if (isAdminPage) {
    return null;
  }

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link href="/">
          <span className="text-2xl font-bold text-blue-600 cursor-pointer">
            igetalibar
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          <Link href="/">
            <span className="text-gray-700 hover:text-blue-600 cursor-pointer">
              Home
            </span>
          </Link>
          <Link href="/products/iget">
            <span className="text-gray-700 hover:text-blue-600 cursor-pointer">
              IGET
            </span>
          </Link>
          <Link href="/faq">
            <span className="text-gray-700 hover:text-blue-600 cursor-pointer">
              FAQ
            </span>
          </Link>
          <Link href="/buying-guide">
            <span className="text-gray-700 hover:text-blue-600 cursor-pointer">
              Buying Guide
            </span>
          </Link>
          <Link href="/recommendation">
            <span className="text-gray-700 hover:text-blue-600 cursor-pointer">
              Quiz
            </span>
          </Link>
        </div>

        {/* User Account & Admin Panel Link - Desktop */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link href="/account" className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:text-blue-600 transition-colors">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {user.email?.[0]?.toUpperCase() || "U"}
                </div>
              </Link>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAuthModalOpen(true)}
              >
                Login
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          className="md:hidden"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-gray-700" />
          ) : (
            <Menu className="w-6 h-6 text-gray-700" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div id="mobile-navigation" className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-3">
          <Link href="/">
            <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2">
              Home
            </span>
          </Link>
          <Link href="/products/iget">
            <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2">
              IGET
            </span>
          </Link>
          <Link href="/faq">
            <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2">
              FAQ
            </span>
          </Link>
          <Link href="/buying-guide">
            <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2">
              Buying Guide
            </span>
          </Link>
          <Link href="/recommendation">
            <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2">
              Quiz
            </span>
          </Link>
          {user ? (
            <>
              <Link href="/account">
                <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2 px-3">
                  Account
                </span>
              </Link>
              {user.role === "admin" && (
                <Link href="/admin/inventory">
                  <span className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2 px-3 bg-blue-100 rounded-lg border border-blue-300">
                    Admin Panel
                  </span>
                </Link>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="block text-gray-700 hover:text-blue-600 cursor-pointer py-2 px-3"
              >
                Login
              </button>
            </>
          )}
        </div>
      )}

      {isAuthModalOpen && (
        <Suspense fallback={null}>
          <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
        </Suspense>
      )}
    </nav>
  );
}

function App() {
  const [location] = useLocation();
  const isAdminPage = location.startsWith('/admin');
  
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <Toaster />
        <Navigation />
        <main id="main-content">
          <Router />
        </main>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
